import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../services/api';
import { theme } from '../theme';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { Plus, ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';

type Post = {
  id: string;
  title: string;
  platform: string | null;
  scheduled_at: string | null;
  status: string;
};

const platformColors: Record<string, string> = {
  facebook: '#3B82F6',
  instagram: '#EC4899',
  youtube: '#EF4444',
  linkedin: '#0EA5E9',
  whatsapp: '#22C55E',
  twitter: '#94A3B8',
};

function platformIcon(p: string): string {
  const m: Record<string, string> = {
    facebook: '📘',
    instagram: '📸',
    youtube: '▶️',
    linkedin: '💼',
    whatsapp: '💬',
    twitter: '🐦',
  };
  return m[p.toLowerCase()] || '📱';
}

function statusColor(st: string): string {
  const s = st.toLowerCase();
  if (s === 'published') return theme.success;
  if (s === 'failed') return theme.danger;
  if (s === 'draft') return theme.textMuted;
  return '#3B82F6';
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

function startOfWeekSunday(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const day = x.getDay();
  x.setDate(x.getDate() - day);
  return x;
}

function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

type ViewMode = 'month' | 'week';

export function CalendarPage() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [weekAnchor, setWeekAnchor] = useState(() => startOfWeekSunday(now));
  const [view, setView] = useState<ViewMode>('week');
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [bizId, setBizId] = useState<string | null>(null);
  const [filterPlat, setFilterPlat] = useState('');
  const [modal, setModal] = useState(false);
  const [detail, setDetail] = useState<Post | null>(null);
  const [form, setForm] = useState({
    title: '',
    platform: 'instagram',
    scheduled_at: '',
  });

  const load = useCallback(async () => {
    const { data: b } = await api.get<{ business: { id: string } | null }>('/business/me');
    if (!b.business) {
      setLoading(false);
      return;
    }
    setBizId(b.business.id);
    const { data } = await api.get<{ posts: Post[] }>('/calendar/month', {
      params: { year, month },
    });
    setPosts(data.posts);
    setLoading(false);
  }, [year, month]);

  useEffect(() => {
    void load().catch(() => setLoading(false));
  }, [load]);

  useEffect(() => {
    if (view !== 'week') return;
    setYear(weekAnchor.getFullYear());
    setMonth(weekAnchor.getMonth() + 1);
  }, [view, weekAnchor]);

  const filteredPosts = useMemo(() => {
    return posts.filter((p) => {
      if (filterPlat && (p.platform || '').toLowerCase() !== filterPlat.toLowerCase()) return false;
      return true;
    });
  }, [posts, filterPlat]);

  const byDayMonth = useMemo(() => {
    const m = new Map<number, Post[]>();
    for (const p of filteredPosts) {
      const d = p.scheduled_at ? new Date(p.scheduled_at) : null;
      if (!d || d.getFullYear() !== year || d.getMonth() + 1 !== month) continue;
      const day = d.getDate();
      if (!m.has(day)) m.set(day, []);
      m.get(day)!.push(p);
    }
    return m;
  }, [filteredPosts, year, month]);

  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => addDays(weekAnchor, i));
  }, [weekAnchor]);

  const postsByDayKey = useMemo(() => {
    const m = new Map<string, Post[]>();
    for (const p of filteredPosts) {
      if (!p.scheduled_at) continue;
      const d = new Date(p.scheduled_at);
      d.setHours(0, 0, 0, 0);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push(p);
    }
    return m;
  }, [filteredPosts]);

  const firstDow = new Date(year, month - 1, 1).getDay();
  const totalDays = daysInMonth(year, month);
  const cells: (number | null)[] = [...Array(firstDow).fill(null)];
  for (let d = 1; d <= totalDays; d++) cells.push(d);

  const weekLabel = `${weekDays[0].toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })} – ${weekDays[6].toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' })}`;

  if (loading) return <LoadingSpinner />;
  if (!bizId) {
    return (
      <div className="odc-page relative z-10 space-y-4 odc-card max-w-lg p-10 text-center" style={{ transform: 'none' }}>
        <p style={{ color: 'var(--muted)' }}>Create a business to use the content calendar.</p>
      </div>
    );
  }

  return (
    <div className="odc-page relative z-10 space-y-8">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border px-3 py-1" style={{ borderColor: 'var(--border2)', background: 'var(--card)' }}>
            <CalendarDays className="h-3.5 w-3.5" style={{ color: theme.secondary }} />
            <span className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: theme.secondary }}>
              Schedule
            </span>
          </div>
          <h1 className="mt-3 text-2xl font-bold md:text-3xl" style={{ color: 'var(--text)' }}>
            Content calendar
          </h1>
          <p className="mt-1 max-w-lg text-sm" style={{ color: 'var(--muted)' }}>
            Week or month view — scheduled posts with platform icons and status at a glance.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-xl border p-0.5" style={{ borderColor: 'var(--border)', background: 'var(--bg2)' }}>
            {(['week', 'month'] as const).map((v) => (
              <button
                key={v}
                type="button"
                className={`rounded-lg px-3 py-1.5 text-xs font-bold capitalize transition ${view === v ? 'odc-pill-tab active' : ''}`}
                style={
                  view === v
                    ? { background: `${theme.primary}28`, color: 'var(--text)', borderColor: theme.primary }
                    : { color: 'var(--muted)' }
                }
                onClick={() => {
                  setView(v);
                  if (v === 'week') {
                    setWeekAnchor(startOfWeekSunday(new Date(year, month - 1, 15)));
                  }
                }}
              >
                {v}
              </button>
            ))}
          </div>
          <select
            className="odc-select w-auto !py-2 text-sm"
            value={filterPlat}
            onChange={(e) => setFilterPlat(e.target.value)}
          >
            <option value="">All platforms</option>
            {['facebook', 'instagram', 'youtube', 'linkedin', 'whatsapp'].map((p) => (
              <option key={p} value={p}>
                {platformIcon(p)} {p}
              </option>
            ))}
          </select>
          <button type="button" className="odc-btn-primary" onClick={() => setModal(true)}>
            <Plus className="h-4 w-4" />
            Add post
          </button>
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          className="odc-btn-ghost !p-2"
          style={{ color: 'var(--text)' }}
          aria-label="Previous"
          onClick={() => {
            if (view === 'month') {
              if (month === 1) {
                setMonth(12);
                setYear((y) => y - 1);
              } else setMonth((m) => m - 1);
            } else {
              setWeekAnchor((w) => addDays(w, -7));
            }
          }}
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <span className="min-w-[200px] text-center text-sm font-bold sm:text-base" style={{ color: 'var(--text)' }}>
          {view === 'month'
            ? new Date(year, month - 1).toLocaleString('default', { month: 'long', year: 'numeric' })
            : weekLabel}
        </span>
        <button
          type="button"
          className="odc-btn-ghost !p-2"
          style={{ color: 'var(--text)' }}
          aria-label="Next"
          onClick={() => {
            if (view === 'month') {
              if (month === 12) {
                setMonth(1);
                setYear((y) => y + 1);
              } else setMonth((m) => m + 1);
            } else {
              setWeekAnchor((w) => addDays(w, 7));
            }
          }}
        >
          <ChevronRight className="h-5 w-5" />
        </button>
        <button
          type="button"
          className="odc-btn-ghost ml-auto !py-2 text-xs"
          onClick={() => {
            const t = new Date();
            setYear(t.getFullYear());
            setMonth(t.getMonth() + 1);
            setWeekAnchor(startOfWeekSunday(t));
          }}
        >
          Today
        </button>
      </div>

      {view === 'week' ? (
        <div className="odc-card grid grid-cols-7 gap-1 !p-2 sm:gap-2 sm:!p-4" style={{ transform: 'none' }}>
          {weekDays.map((d) => {
            const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
            const dayPosts = postsByDayKey.get(key) || [];
            const isToday = sameDay(d, new Date());
            return (
              <div
                key={key}
                className="flex min-h-[140px] flex-col rounded-xl border p-2 text-left sm:min-h-[200px]"
                style={{
                  borderColor: isToday ? theme.primary : 'var(--border)',
                  background: isToday ? `${theme.primary}0d` : 'var(--bg2)',
                }}
              >
                <div className="text-[10px] font-bold uppercase sm:text-xs" style={{ color: 'var(--muted)' }}>
                  {d.toLocaleDateString('en-IN', { weekday: 'short' })}
                </div>
                <div className="text-lg font-black" style={{ color: 'var(--text)' }}>
                  {d.getDate()}
                </div>
                <div className="mt-2 flex flex-1 flex-col gap-1 overflow-y-auto">
                  {dayPosts.map((p) => {
                    const plat = (p.platform || '').toLowerCase();
                    const col = platformColors[plat] || theme.primary;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        className="flex w-full items-start gap-1 rounded-lg border px-1.5 py-1 text-left text-[9px] font-semibold transition hover:border-[var(--border2)] sm:text-[10px]"
                        style={{
                          borderColor: 'var(--border)',
                          background: `${col}18`,
                          color: 'var(--text)',
                        }}
                        onClick={() => setDetail(p)}
                      >
                        <span className="shrink-0">{platformIcon(plat)}</span>
                        <span className="line-clamp-2">{p.title}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="odc-card grid grid-cols-7 gap-1 !p-2 text-center text-xs font-bold sm:gap-2 sm:!p-4" style={{ transform: 'none' }}>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
            <div key={d} style={{ color: 'var(--muted)' }}>
              {d}
            </div>
          ))}
          {cells.map((day, i) => (
            <div
              key={i}
              className="min-h-[80px] rounded-xl border p-1 text-left transition hover:border-[var(--border2)] sm:min-h-[100px] sm:p-2"
              style={{
                borderColor: day ? 'var(--border)' : 'transparent',
                background: day ? 'var(--bg2)' : 'transparent',
              }}
            >
              {day ? (
                <>
                  <div className="text-[10px] font-bold sm:text-xs" style={{ color: 'var(--muted)' }}>
                    {day}
                  </div>
                  <div className="mt-1 space-y-0.5">
                    {(byDayMonth.get(day) || []).slice(0, 3).map((p) => {
                      const plat = (p.platform || '').toLowerCase();
                      const col = platformColors[plat] || theme.primary;
                      return (
                        <button
                          key={p.id}
                          type="button"
                          className="flex w-full items-center gap-0.5 truncate rounded px-1 py-0.5 text-left text-[9px] font-semibold sm:text-[10px]"
                          style={{
                            background: `${col}33`,
                            color: col,
                          }}
                          onClick={() => setDetail(p)}
                        >
                          <span className="shrink-0">{platformIcon(plat)}</span>
                          <span className="truncate">{p.title}</span>
                        </button>
                      );
                    })}
                    {(byDayMonth.get(day) || []).length > 3 ? (
                      <span className="text-[9px]" style={{ color: 'var(--muted)' }}>
                        +{(byDayMonth.get(day) || []).length - 3} more
                      </span>
                    ) : null}
                  </div>
                </>
              ) : null}
            </div>
          ))}
        </div>
      )}

      <div className="odc-card flex flex-wrap gap-4 !p-4" style={{ transform: 'none' }}>
        <span className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
          Status
        </span>
        {['scheduled', 'published', 'failed', 'draft'].map((s) => (
          <span key={s} className="flex items-center gap-2 text-xs capitalize" style={{ color: 'var(--muted)' }}>
            <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: statusColor(s) }} />
            {s}
          </span>
        ))}
      </div>

      {detail ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setDetail(null)}>
          <div className="odc-card w-full max-w-md !p-6" style={{ transform: 'none' }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3">
              <span className="text-2xl">{platformIcon(detail.platform || '')}</span>
              <div>
                <h3 className="text-lg font-bold" style={{ color: 'var(--text)' }}>
                  {detail.title}
                </h3>
                <p className="mt-1 text-sm capitalize" style={{ color: 'var(--muted)' }}>
                  {(detail.platform || '—').toString()}
                </p>
              </div>
            </div>
            <p className="mt-4 text-sm">
              <span className={`odc-badge capitalize`} style={{ background: `${statusColor(detail.status)}22`, color: statusColor(detail.status) }}>
                {detail.status}
              </span>
            </p>
            <p className="mt-3 text-xs" style={{ color: 'var(--muted)' }}>
              {detail.scheduled_at ? new Date(detail.scheduled_at).toLocaleString() : 'Unscheduled'}
            </p>
            <button type="button" className="odc-btn-ghost mt-6 w-full justify-center" onClick={() => setDetail(null)}>
              Close
            </button>
          </div>
        </div>
      ) : null}

      {modal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="odc-card w-full max-w-md !p-6" style={{ transform: 'none' }}>
            <h3 className="font-bold" style={{ color: 'var(--text)' }}>
              Schedule post
            </h3>
            <input
              className="odc-input mt-4 !py-2.5 text-sm"
              placeholder="Title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
            <select
              className="odc-select mt-3 !py-2.5 text-sm"
              value={form.platform}
              onChange={(e) => setForm({ ...form, platform: e.target.value })}
            >
              {['facebook', 'instagram', 'youtube', 'linkedin', 'whatsapp'].map((p) => (
                <option key={p} value={p}>
                  {platformIcon(p)} {p}
                </option>
              ))}
            </select>
            <input
              type="datetime-local"
              className="odc-input mt-3 !py-2.5 text-sm"
              value={form.scheduled_at}
              onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })}
            />
            <div className="mt-6 flex justify-end gap-2">
              <button type="button" className="odc-btn-ghost !py-2.5 text-sm" onClick={() => setModal(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="odc-btn-primary !py-2.5 text-sm"
                onClick={() =>
                  void (async () => {
                    if (!form.title.trim()) return;
                    const iso = form.scheduled_at ? new Date(form.scheduled_at).toISOString() : undefined;
                    await api.post('/calendar/posts', {
                      businessId: bizId,
                      title: form.title,
                      platform: form.platform,
                      scheduled_at: iso,
                    });
                    setModal(false);
                    setForm({ title: '', platform: 'instagram', scheduled_at: '' });
                    await load();
                  })()
                }
              >
                Save
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
